import React, { useEffect, useRef, useState } from 'react';
import { ShortsMode, DEFAULT_SHORTS_MODE } from '../../utils/shorts';
import {
  TranscriptionFields,
  ShortsModePicker,
  SummaryVideoOption,
  useWhisperModels,
} from '../ShortsSetupFields';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export interface GenerateShortsTarget {
  /** The job that produced the video — a caption render, or a clip export made from one. */
  jobId: string;
  /** Suggested name, normally the caption's own with a suffix. */
  defaultName: string;
  /**
   * Whether this video still runs on the transcript's timeline. A caption render does; an edited
   * clip does not, and reusing a transcript against it produces clips at timestamps that no
   * longer exist — so the default flips and the warning shows.
   */
  timelinePreserved: boolean;
}

interface GenerateShortsModalProps {
  open: boolean;
  target: GenerateShortsTarget | null;
  projectId: string;
  workspaceId: string;
  onClose: () => void;
  onGenerated: (result: { videoId: string; jobId: string }) => void;
}

const GenerateShortsModal: React.FC<GenerateShortsModalProps> = ({
  open, target, projectId, workspaceId, onClose, onGenerated,
}) => {
  const [name, setName] = useState('');
  const [reuse, setReuse] = useState(true);
  const [createSummary, setCreateSummary] = useState(false);
  const [mode, setMode] = useState<ShortsMode>(DEFAULT_SHORTS_MODE);
  const [language, setLanguage] = useState('auto');
  const { models: whisperModels, model: whisperModel, setModel: setWhisperModel } = useWhisperModels(open);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // The default follows the source: reuse where the timeline still matches, re-transcribe where
  // it does not. Either is always selectable — the choice is the author's.
  useEffect(() => {
    if (!open || !target) return;
    setName(target.defaultName);
    setReuse(target.timelinePreserved);
    setMode(DEFAULT_SHORTS_MODE);
    setCreateSummary(false);
    setError(null);
    setSubmitting(false);
    requestAnimationFrame(() => nameRef.current?.focus());
  }, [open, target]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape' && !submitting) onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose, submitting]);

  if (!open || !target) return null;

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE}/api/projects/${projectId}/caption-workspaces/${workspaceId}/renders/${target.jobId}/shorts`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: name.trim(),
            reuseTranscript: reuse,
            createSummary,
            ...(mode === 'studio' ? { mode } : {}),
            ...(reuse ? {} : { language, whisperModel }),
          }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Failed to generate shorts');
      onGenerated({ videoId: data.videoId, jobId: data.jobId });
    } catch (e: any) {
      setError(e.message || 'Failed to generate shorts');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => { if (!submitting) onClose(); }}>
      <div className="modal-container generate-shorts-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-text">
            <h2>Generate Shorts</h2>
            <p className="modal-subtitle">Create a shorts item in this project from this render.</p>
          </div>
          <button className="modal-close" onClick={onClose} disabled={submitting}>✕</button>
        </div>

        {error && <div className="error-banner">⚠️ {error}</div>}

        <div className="modal-field">
          <label>Name</label>
          <input
            ref={nameRef}
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Name for the shorts item..."
          />
        </div>

        <div className="modal-field">
          <label>Transcription</label>

          <label className={`opt-row ${reuse ? 'opt-row--active' : ''}`}>
            <input
              type="radio"
              name="gs-transcription"
              checked={reuse}
              onChange={() => setReuse(true)}
            />
            <span className="opt-row-body">
              <span className="opt-row-title">
                Reuse existing transcript
                {target.timelinePreserved && <span className="opt-row-tag">recommended</span>}
              </span>
              <span className="opt-row-desc">
                Faster. Uses the transcription already generated for this caption. Best when the
                render covers the same footage.
              </span>
            </span>
          </label>

          <label className={`opt-row ${!reuse ? 'opt-row--active' : ''}`}>
            <input
              type="radio"
              name="gs-transcription"
              checked={!reuse}
              onChange={() => setReuse(false)}
            />
            <span className="opt-row-body">
              <span className="opt-row-title">
                Transcribe again
                {!target.timelinePreserved && <span className="opt-row-tag">recommended</span>}
              </span>
              <span className="opt-row-desc">
                Slower. Re-analyses the audio of this exact video. Choose this for edited or
                trimmed clips.
              </span>
            </span>
          </label>

          {/* Nothing downstream can detect a mismatched transcript, so an edited source says so
              here rather than failing later with clips cut at timestamps that no longer exist. */}
          {!target.timelinePreserved && reuse && (
            <div className="gs-warning">
              ⚠️ This video was edited, so the existing transcript may not match its timeline.
              Reusing it can produce clips at the wrong timestamps.
            </div>
          )}
        </div>

        {/* Language and model steer Whisper, which only runs when the transcript is not reused. */}
        {!reuse && (
          <TranscriptionFields
            language={language}
            onLanguageChange={setLanguage}
            model={whisperModel}
            onModelChange={setWhisperModel}
            models={whisperModels}
            helpText="Used only when the transcript is re-generated."
          />
        )}

        <SummaryVideoOption checked={createSummary} onChange={setCreateSummary} />

        <ShortsModePicker value={mode} onChange={setMode} name="gs-shorts-mode" />

        <div className="modal-actions">
          <button className="btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn-primary" onClick={submit} disabled={submitting || !name.trim()}>
            {submitting ? '⏳ Starting…' : 'Generate →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GenerateShortsModal;
